/**
 * Tenant-admin view of the EstimateSnapshot — same line items the
 * customer sees, plus inline Edit + override-history list.
 */

import { useEffect, useState } from "react";

import { LineItemEditor } from "@/components/admin/LineItemEditor";
import { getEstimateSnapshot, listOverrides } from "@starz/shared/api/sdk.gen";
import type { EstimateSnapshot, LineItemOverride } from "@starz/shared/api/types.gen";

interface Props {
  estimateId: string;
  isReadOnly: boolean;
}

function fmt(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export function AdminSnapshotPanel({ estimateId, isReadOnly }: Props) {
  const [snapshot, setSnapshot] = useState<EstimateSnapshot | null>(null);
  const [overrides, setOverrides] = useState<LineItemOverride[]>([]);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [s, o] = await Promise.all([
        getEstimateSnapshot({ estimateId }),
        listOverrides({ estimateId }),
      ]);
      if (cancelled) return;
      if ("data" in s) setSnapshot(s.data);
      if ("data" in o) setOverrides(o.data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId]);

  if (loading) {
    return (
      <section className="rounded-xl border border-starz-gold/20 bg-white p-5 text-sm text-warm-gray shadow-sm">
        Loading estimate snapshot…
      </section>
    );
  }
  if (snapshot == null) {
    return (
      <section className="rounded-xl border border-starz-gold/20 bg-white p-5 text-sm text-warm-gray shadow-sm">
        No snapshot has been computed yet.
      </section>
    );
  }

  return (
    <section
      aria-label="Estimate snapshot"
      className="space-y-4 rounded-xl border border-starz-gold/20 bg-white p-5 shadow-sm"
    >
      <header>
        <h3 className="font-serif text-xl text-starz-navy">Line items</h3>
        <p className="mt-1 text-xs text-warm-gray">
          ZIP factor used: <span className="font-mono">{snapshot.zip_factor_used.toFixed(3)}</span>
          {snapshot.zip_used ? ` (${snapshot.zip_used})` : " (default)"}.
        </p>
      </header>
      <ul className="divide-y divide-starz-gold/10">
        {snapshot.line_items.map((li) => (
          <li key={li.line_id} className="py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <span className="text-base font-medium text-starz-navy">
                {li.display_name}
                {li.source === "admin_adjusted" && (
                  <span className="ml-2 rounded bg-amber-50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-900">
                    Adjusted
                  </span>
                )}
              </span>
              <span className="font-mono text-base text-starz-charcoal">
                {fmt(li.line_low_cents)} – {fmt(li.line_high_cents)}
              </span>
            </div>
            <div className="mt-1 text-xs text-warm-gray">
              {li.qty_low != null && li.qty_high != null
                ? `${Math.round(li.qty_low)} – ${Math.round(li.qty_high)} ${li.unit}`
                : li.unit}{" "}
              × ${(li.unit_price_cents / 100).toFixed(2)}/{li.unit}
              {li.zip_factor !== 1 ? ` × ${li.zip_factor.toFixed(3)} ZIP` : ""}
            </div>
            {!isReadOnly && editingLineId !== li.line_id && (
              <button
                type="button"
                onClick={() => setEditingLineId(li.line_id)}
                className="mt-2 text-xs font-semibold uppercase tracking-widest text-starz-gold hover:underline"
              >
                Edit
              </button>
            )}
            {!isReadOnly && editingLineId === li.line_id && (
              <div className="mt-2">
                <LineItemEditor
                  estimateId={estimateId}
                  line={li}
                  onCancel={() => setEditingLineId(null)}
                  onSaved={async (newSnapshot) => {
                    setSnapshot(newSnapshot);
                    setEditingLineId(null);
                    const o = await listOverrides({ estimateId });
                    if ("data" in o) setOverrides(o.data);
                  }}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
      <footer className="border-t border-starz-gold/15 pt-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-semibold uppercase tracking-widest text-starz-charcoal">
            Subtotal
          </span>
          <span className="font-mono text-lg text-starz-navy">
            {fmt(snapshot.subtotal_low_cents)} – {fmt(snapshot.subtotal_high_cents)}
          </span>
        </div>
      </footer>
      {overrides.length > 0 && (
        <details className="rounded border border-starz-gold/15 bg-starz-cream/30 p-4">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-widest text-starz-charcoal">
            Override history ({overrides.length})
          </summary>
          <ul className="mt-3 space-y-2 text-xs text-starz-charcoal">
            {overrides.map((o) => (
              <li key={o.override_id} className="border-l-2 border-starz-gold/40 pl-3">
                <div>
                  <strong>Line {o.line_id.slice(0, 8)}</strong> — {o.reason}
                </div>
                <div className="mt-0.5 font-mono text-warm-gray">
                  {o.before_unit_price_cents != null && (
                    <>
                      ${(o.before_unit_price_cents / 100).toFixed(2)} → $
                      {((o.after_unit_price_cents ?? 0) / 100).toFixed(2)}
                      {" · "}
                    </>
                  )}
                  {new Date(o.overridden_at).toLocaleString()}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
